import { CardTable, EntityName, Link, TableBottomCell } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  CryptoIcon,
  Flex,
  Box,
  CurrencyValue,
  PercentageValue,
  TableSkeleton
} from '@blockanalitica/ui';
import { useApi, PaginatedResponse } from '@/api';
import { useNetwork } from '@/hooks';

type CollateralData = {
  address: string;
  market_address: string;
  name: string;
  underlying_symbol: string;
  market_symbol: string;
  market_underlying_address: string;
  total_supply: string | null;
  total_supply_token: string | null;
  total_supply_change: string | null;
  supply_cap: string | number | null;
  supply_cap_token: string | null;
  collateral_factor: string | null;
  collateral_factor_change: string | null;
  liquidation_threshold: string | null;
  liquidation_threshold_change: string | null;
  utilization: string | null;
  utilization_change: string | null;
  total_supply_token_change: string | null;
};

export default function CollateralsTable({
  marketAddress
}: {
  marketAddress: string;
}) {
  const network = useNetwork();
  const { sorting } = useTableProps({
    initialSorting: '-total_supply_token'
  });

  const [data, error] = useApi<PaginatedResponse<CollateralData[]>>({
    url: `/markets/${marketAddress}/collaterals/`,
    queryParams: {
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} />;
  }
  const columns: TableColumn<CollateralData>[] = [
    {
      header: 'Asset',
      getCell: (row) => (
        <EntityName
          entitySize="5"
          symbol1={
            <CryptoIcon
              name={row.underlying_symbol}
              address={row.address}
              network={network}
              size="5"
            />
          }>
          <Flex flexDirection="column" gap="3xs">
            <Box>{row.underlying_symbol}</Box>
            <Box color="muted" fontSize="-2">
              {row.name}
            </Box>
          </Flex>
        </EntityName>
      )
    },
    {
      header: 'Total Supply',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_supply}
          options={{
            iconSize: '1',
            currency: {
              name: row.underlying_symbol,
              address: row.address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell>
          <CurrencyValue
            value={row.total_supply_token}
            options={{
              iconSize: '0',
              currency: {
                name: row.market_symbol,
                address: row.market_underlying_address,
                network: network
              }
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'total_supply_token',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: '1D Change',
      getCell: (row) => (
        <>
          {Number(row.total_supply_change) == 0 ? (
            <Box height="100%"></Box>
          ) : (
            <CurrencyValue
              value={row.total_supply_change}
              options={{
                iconSize: '1',
                currency: {
                  name: row.underlying_symbol,
                  address: row.address,
                  network: network
                },
                trend: true
              }}
            />
          )}
        </>
      ),
      getCellBottom: (row) => (
        <TableBottomCell>
          <CurrencyValue
            value={row.total_supply_token_change}
            options={{
              iconSize: '0',
              trendIconSize: '-2',
              currency: {
                name: row.market_symbol,
                address: row.market_underlying_address,
                network: network
              },
              trend: true
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'total_supply_token_change',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Supply Cap',
      getCell: (row) => (
        <CurrencyValue
          value={row.supply_cap}
          options={{
            iconSize: '1',
            currency: {
              name: row.underlying_symbol,
              address: row.address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell>
          <CurrencyValue
            value={row.supply_cap_token}
            options={{
              iconSize: '0',
              currency: {
                name: row.market_symbol,
                address: row.market_underlying_address,
                network: network
              }
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'supply_cap_token',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Utilization',
      getCell: (row) => <PercentageValue value={row.utilization} />,
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.utilization_change}>
          <PercentageValue
            value={row.utilization_change}
            options={{ trend: true, iconSize: '-1' }}
          />
        </TableBottomCell>
      ),
      sortingField: 'utilization',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Coll. Factor',
      getCell: (row) => <PercentageValue value={row.collateral_factor} />,
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.collateral_factor_change}>
          <PercentageValue
            value={row.collateral_factor_change}
            options={{ trend: true, iconSize: '-1' }}
          />
        </TableBottomCell>
      ),
      sortingField: 'collateral_factor',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liq. Factor',
      getCell: (row) => <PercentageValue value={row.liquidation_threshold} />,
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.liquidation_threshold_change}>
          <PercentageValue
            value={row.liquidation_threshold_change}
            options={{ trend: true, iconSize: '-1' }}
          />
        </TableBottomCell>
      ),
      sortingField: 'liquidation_threshold',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <CardTable<CollateralData>
      title="Collateral Assets"
      rows={data['results']}
      columns={columns}
      getKey={(row) => row.address}
      rowLinkProps={{
        to: (row) => `${row.address}`,
        // @ts-ignore
        linkComponent: Link
      }}
      sorting={sorting}
    />
  );
}
